'use client';

import { useCallback } from 'react';
import dynamic from 'next/dynamic';
import { registerCompletion } from 'monacopilot';
import { useSite } from '@/lib/site-context';

import '@/app/App.css';
import './CodeEditorPanel.scss';
import { Button } from '@/app/components';

// Dynamically import Monaco Editor to avoid SSR issues
const Editor = dynamic(() => import('@monaco-editor/react'), {
  ssr: false,
  loading: () => <div className="editor-loading">Loading editor...</div>
});

interface CodeEditorPanelProps {
  code: string;
  onChange: (code: string | undefined) => void;
  onExecute: () => void;
  isExecuting: boolean;
  autoCompile: boolean;
  onAutoCompileChange: (enabled: boolean) => void;
}

export default function CodeEditorPanel({
  code,
  onChange,
  onExecute,
  isExecuting,
  autoCompile,
  onAutoCompileChange
}: CodeEditorPanelProps) {
  const site = useSite();
  const handleEditorChange = useCallback((value: string | undefined) => {
    onChange(value);
  }, [onChange]);
  const handleEditorDidMount = useCallback((editor: any, monaco: any) => {
    // Add keyboard shortcut for Alt+Enter to run code
    editor.addCommand(monaco.KeyMod.Alt | monaco.KeyCode.Enter, () => {
      if (!isExecuting) {
        onExecute();
      }
    });

    if (site.feature.aiCompletions) {
      try {
        registerCompletion(monaco, editor, {
          language: site.monacoLanguage,
          endpoint: '/api/code-completion',
        });
      } catch (error) {
        console.warn('Failed to register AI completion:', error);
      }
    }
  }, [onExecute, isExecuting, site]);
  return (
    <div className="code-panel">
      <div className="panel-header">
        <div className="panel-title">
          <h3>
            {site.codeLanguageLabel} Code Editor
          </h3>
        </div>
        <div className="panel-controls">
          <label className="autocompile-checkbox">
            <input
              type="checkbox"
              checked={autoCompile}
              onChange={(e) => onAutoCompileChange(e.target.checked)}
              title="Automatically run code after changes"
            />
            <span>Auto-run</span>
          </label>
          <Button
            onClick={onExecute}
            disabled={isExecuting}
            className="run-btn"
            title="Run Code (Alt+Enter)"
          >
            {isExecuting ? "Executing..." : "Run ⯈"}
          </Button>
        </div>
      </div>
      <div className="editor-container">
        <Editor
          height="100%"
          defaultLanguage={site.monacoLanguage}
          value={code}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            wordWrap: 'on'
          }}
        />
      </div>
    </div>
  );
}
